<template>
    <div class="container-fluid">
      
      <!-- Header with Toolbar -->
      <div class="row my-3">
        <div class="col-12">
          <div class="d-flex justify-content-between align-items-start gap-3 flex-wrap">
            <div>
              <h2 class="mb-2">Dashboard</h2>
              
              <!-- Technician Selector (hidden for now)
              <div class="mb-2">
                <label class="form-label fw-bold mb-1">Technicians</label>
                <tech-ac 
                  v-model="selectedTechId" 
                  placeholder="Select technicians..."
                  @change="onTechChange"
                />
              </div>
              
              <div v-if="selectedTechnicians.length > 0" class="d-flex flex-wrap gap-2 mt-2">
                <span 
                  v-for="tech in selectedTechnicians" 
                  :key="tech.Id"
                  class="badge bg-primary d-flex align-items-center gap-2"
                  style="font-size: 0.9rem; padding: 0.4rem 0.6rem;"
                >
                  {{ tech.TechnicianName }}
                  <button 
                    type="button" 
                    class="btn-close btn-close-white" 
                    style="font-size: 0.6rem;"
                    @click="removeTechnician(tech.Id)"
                    aria-label="Remove"
                  ></button>
                </span>
              </div>
              -->
            </div>
            
            <!-- Action Buttons -->
            <div class="d-flex gap-2">
              <button class="btn btn-outline-primary" @click="openRequestModal">
                <i class="fa fa-plus me-1"></i> Add Request
              </button>
              <button class="btn btn-outline-success" @click="openTaskModal">
                <i class="fa fa-tasks me-1"></i> Add Task
              </button>
              <button class="btn btn-outline-warning" @click="openWorkModal">
                <i class="fa fa-wrench me-1"></i> Add Work
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Tabs -->
      <div class="row">
        <div class="col-12">
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'work-management' }"
                href="#" 
                @click.prevent="activeTab = 'work-management'"
              >
                <i class="fa fa-briefcase me-1"></i> Work Management
              </a>
            </li>
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'task-management' }"
                href="#" 
                @click.prevent="activeTab = 'task-management'"
              >
                <i class="fa fa-tasks me-1"></i> Task Management
              </a>
            </li>
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'statistics' }"
                href="#" 
                @click.prevent="activeTab = 'statistics'"
              >
                <i class="fa fa-chart-bar me-1"></i> Technician Statistics
              </a>
            </li>
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'timesheets' }"
                href="#" 
                @click.prevent="activeTab = 'timesheets'"
              >
                <i class="fa fa-clock me-1"></i> Timesheets
              </a>
            </li>
            <li class="nav-item">
              <a 
                class="nav-link" 
                :class="{ active: activeTab === 'stalled-jobs' }"
                href="#" 
                @click.prevent="activeTab = 'stalled-jobs'"
              >
                <i class="fa fa-pause-circle me-1"></i> Stalled Jobs
              </a>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- Tab Content -->
      <div class="row">
        <div class="col-12">
          <!-- Work Management Tab -->
          <div v-show="activeTab === 'work-management'">
            <work-report :selected-tech-ids="selectedTechnicianIds" />
          </div>
          
          <!-- Task Management Tab -->
          <div v-show="activeTab === 'task-management'">
            <task-management-report :selected-tech-ids="selectedTechnicianIds" />
          </div>
          
          <!-- Stalled Jobs Tab -->
          <div v-show="activeTab === 'stalled-jobs'">
            <stalled-jobs-report />
          </div>
          
          <!-- Technician Statistics Tab -->
          <div v-show="activeTab === 'statistics'">

            <!-- Stats Cards -->
      <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4 mb-4">
        
        <!-- Open Jobs Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Open Jobs</h6>
                  <h2 class="card-title mb-0">{{ openJobsCount }}</h2>
                </div>
                <div class="rounded-circle bg-primary bg-opacity-10 p-3">
                  <i class="fas fa-briefcase fa-lg text-primary"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i> 
                <a href="#/job/list" class="text-decoration-none">View all jobs</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Jobs Closed This Week Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Closed This Week</h6>
                  <h2 class="card-title mb-0">{{ closedThisWeekCount }}</h2>
                </div>
                <div class="rounded-circle bg-success bg-opacity-10 p-3">
                  <i class="fas fa-check-circle fa-lg text-success"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">Since {{ weekStartDate }}</p>
            </div>
          </div>
        </div>

        <!-- Old Jobs Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Old Jobs (30+ Days)</h6>
                  <h2 class="card-title mb-0">{{ oldJobsCount }}</h2>
                </div>
                <div class="rounded-circle bg-warning bg-opacity-10 p-3">
                  <i class="fas fa-exclamation-triangle fa-lg text-warning"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">Require attention</p>
            </div>
          </div>
        </div>

        <!-- Hours This Week Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Hours This Week</h6>
                  <h2 class="card-title mb-0">{{ hoursThisWeek }}</h2>
                </div>
                <div class="rounded-circle bg-info bg-opacity-10 p-3">
                  <i class="fas fa-clock fa-lg text-info"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/time/list" class="text-decoration-none">View time entries</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Running Clocks Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Running Clocks</h6>
                  <h2 class="card-title mb-0">{{ runningClocksCount }}</h2>
                </div>
                <div class="rounded-circle bg-success bg-opacity-10 p-3">
                  <i class="fas fa-play-circle fa-lg text-success"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/clock/list" class="text-decoration-none">View clocks</a>
              </p>
            </div>
          </div>
        </div>

        <!-- Pending Parts Card -->
        <div class="col">
          <div class="card h-100 border-0 shadow-sm">
            <div class="card-body">
              <div class="d-flex justify-content-between align-items-start">
                <div>
                  <h6 class="card-subtitle text-muted mb-2">Pending Parts</h6>
                  <h2 class="card-title mb-0">{{ pendingPartsCount }}</h2>
                </div>
                <div class="rounded-circle bg-secondary bg-opacity-10 p-3">
                  <i class="fas fa-cog fa-lg text-secondary"></i>
                </div>
              </div>
              <p class="text-muted small mt-2 mb-0">
                <i class="fas fa-arrow-right"></i>
                <a href="#/part/list" class="text-decoration-none">View parts</a>
              </p>
            </div>
          </div>
        </div>

            </div>

          </div>
          
          <!-- Timesheets Tab -->
          <div v-show="activeTab === 'timesheets'">
            <timesheets-view :selected-tech-ids="selectedTechnicianIds" />
          </div>
        </div>
      </div>
    </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, computed } from "vue";
import WorkReport from "@/components/Reports/WorkReport.vue";
import TaskManagementReport from "@/components/Reports/TaskManagementReport.vue";
import StalledJobsReport from "@/components/Reports/StalledJobsReport.vue";
import TimesheetsView from "@/components/Reports/TimesheetsView.vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ApiService from "@/services/ApiService";
import Emitter from "@/services/Emitter";
import { useAppStore } from "@/stores/AppStore";
import { useTechStore } from "@/stores/TechStore";
import { RequestFilter } from "@/models/RequestModel";
import { TimeModelFilter } from "@/models/TaskModel";
import { PartModelFilter } from "@/models/PartModel";

const appStore = useAppStore();
const techStore = useTechStore();

// Active tab
const activeTab = ref('work-management');

// Technician selection
const selectedTechId = ref('');
const selectedTechnicians = ref<any[]>([]);

const selectedTechnicianIds = computed(() => {
  return selectedTechnicians.value.map(t => t.Id);
});

// Handle technician selection
const onTechChange = (techId: string) => {
  if (!techId) return;
  
  // Find the technician in the store
  const tech = techStore.list[parseInt(techId)];
  if (!tech) return;
  
  // Check if already selected
  const alreadySelected = selectedTechnicians.value.some(t => t.Id === tech.Id);
  if (alreadySelected) {
    selectedTechId.value = '';
    return;
  }
  
  // Add to selection
  selectedTechnicians.value.push(tech);
  selectedTechId.value = '';
};

// Remove technician from selection
const removeTechnician = (techId: number) => {
  selectedTechnicians.value = selectedTechnicians.value.filter(t => t.Id !== techId);
};

// Open modals
const openRequestModal = () => {
  Emitter.emit('open:jobForm');
};

const openTaskModal = () => {
  Emitter.emit('open:taskForm');
};

const openWorkModal = () => {
  Emitter.emit('open:work');
};

// User info
const userName = computed(() => {
  return appStore.account.user?.Email?.split('@')[0] || 'User';
});

const currentUserId = computed(() => {
  return appStore.account.user?.UserId || 0;
});

// Stats
const openJobsCount = ref(0);
const closedThisWeekCount = ref(0);
const oldJobsCount = ref(0);
const hoursThisWeek = ref('0.0');
const runningClocksCount = ref(0);
const pendingPartsCount = ref(0);

// Helper to get week start date
const weekStartDate = computed(() => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1; // Monday as week start
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  return monday.toLocaleDateString();
});

// Calculate date ranges
const getWeekStart = (): Date => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  const diff = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(now);
  monday.setDate(now.getDate() - diff);
  monday.setHours(0, 0, 0, 0);
  return monday;
};

const get30DaysAgo = (): Date => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date;
};

// Load open jobs
const loadOpenJobs = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [1, 2, 3, 4, 5] as Array<Number>; // Active statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    openJobsCount.value = response.Meta.Total || 0;
  } catch (error) {
    console.error('Error loading open jobs:', error);
  }
};

// Load jobs closed this week
const loadClosedThisWeek = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [6, 7] as Array<Number>; // Closed/Completed statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    
    // Filter by completion date this week
    const weekStart = getWeekStart();
    const closedThisWeek = (response.Data || []).filter(job => {
      if (!job.CompletionDate) return false;
      const completionDate = new Date(job.CompletionDate);
      return completionDate >= weekStart;
    });
    
    closedThisWeekCount.value = closedThisWeek.length;
  } catch (error) {
    console.error('Error loading closed jobs:', error);
  }
};

// Load old jobs
const loadOldJobs = async () => {
  try {
    const filter = new RequestFilter();
    filter.Owner = [currentUserId.value as Number];
    filter.Status = [1, 2, 3, 4, 5] as Array<Number>; // Active statuses
    filter.Size = 9999 as Number;
    
    const response = await ApiService.loadRequests(filter, true);
    
    // Filter by create date over 30 days ago
    const thirtyDaysAgo = get30DaysAgo();
    const oldJobs = (response.Data || []).filter(job => {
      if (!job.CreateDate) return false;
      const createDate = new Date(job.CreateDate);
      return createDate < thirtyDaysAgo;
    });
    
    oldJobsCount.value = oldJobs.length;
  } catch (error) {
    console.error('Error loading old jobs:', error);
  }
};

// Load hours this week
const loadHoursThisWeek = async () => {
  try {
    const filter = new TimeModelFilter();
    const weekStart = getWeekStart();
    filter.StartDate = weekStart;
    filter.EndDate = new Date();
    filter.TechId = [currentUserId.value as number];
    filter.Size = 9999;
    
    const response = await ApiService.loadTimes(filter, true);
    
    // Sum up the hours
    const totalMinutes = (response.Data || []).reduce((sum, entry) => {
      return sum + (entry.ActualTime || 0);
    }, 0);
    
    const hours = (totalMinutes / 60).toFixed(1);
    hoursThisWeek.value = hours;
  } catch (error) {
    console.error('Error loading hours:', error);
  }
};

// Load running clocks
const loadRunningClocks = async () => {
  try {
    const clocks = await ApiService.loadClocks(true);
    runningClocksCount.value = clocks.length;
  } catch (error) {
    console.error('Error loading clocks:', error);
  }
};

// Load pending parts
const loadPendingParts = async () => {
  try {
    const filter = new PartModelFilter();
    filter.TechId = [currentUserId.value as number];
    filter.OrderStatus = [1, 2] as Array<number>; // Pending/Ordered statuses
    filter.Size = 9999;
    
    const response = await ApiService.loadParts(filter, true);
    pendingPartsCount.value = response.Meta.Total || 0;
  } catch (error) {
    console.error('Error loading parts:', error);
  }
};

// Load all dashboard data
const loadDashboard = async () => {
  await Promise.all([
    loadOpenJobs(),
    loadClosedThisWeek(),
    loadOldJobs(),
    loadHoursThisWeek(),
    loadRunningClocks(),
    loadPendingParts()
  ]);
};

onMounted(() => {
  loadDashboard();
});

</script>